/**
 * Catch-all for `theailedger.ai/{slug}` branded Founder Affiliate URLs.
 *
 * If the URL `/foo` matches an active Founding Affiliate slug, this calls
 * the lookup endpoint (which records the click + sets the long-lived
 * tal_inf cookie) and redirects to the homepage. Otherwise it falls
 * through to the provided <Fallback /> (typically <ComingSoon /> while
 * pre-launch, or a 404 page post-launch).
 *
 * Lives outside the regular Route table because we want it to share the
 * URL space with every other top-level route — meaning the lookup must
 * only fire after React Router has exhausted its declared routes.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Match a single path segment, lowercase letters/numbers/-/_, 2-32 chars.
const SLUG_RE = /^\/([a-z0-9][a-z0-9_-]{1,30})\/?$/;

export default function BrandedSlugFallback({ children }) {
  const location = useLocation();
  const [state, setState] = useState('checking'); // checking | matched | fallback

  useEffect(() => {
    let cancelled = false;
    const m = location.pathname.match(SLUG_RE);
    if (!m) {
      setState('fallback');
      return () => {};
    }
    const slug = m[1];
    (async () => {
      try {
        const res = await axios.get(`${API}/api/founder-affiliate/lookup/${encodeURIComponent(slug)}`,
                                     { withCredentials: true });
        if (cancelled) return;
        if (res.data?.matched) {
          // Hard navigate to / so the rest of the SPA boots cleanly with the cookie set.
          window.location.replace('/');
          setState('matched');
        } else {
          setState('fallback');
        }
      } catch (_) {
        if (!cancelled) setState('fallback');
      }
    })();
    return () => { cancelled = true; };
  }, [location.pathname]);

  if (state === 'checking' || state === 'matched') {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center" data-testid="branded-slug-checking">
        <div className="text-center">
          <div className="w-10 h-10 mx-auto mb-4 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-neutral-400">Loading…</p>
        </div>
      </div>
    );
  }
  // We reached the fallback because React Router already exhausted every
  // declared route AND (if it looked like a slug) the founder-affiliate
  // lookup returned no match. This is effectively a 404 — tell Google not
  // to index it via a noindex meta so Search Console stops flagging these
  // catch-all pages as "Soft 404" (Feb 2026 SEO fix).
  return (
    <>
      <NoIndexMeta />
      {children}
    </>
  );
}

/**
 * Adds `<meta name="robots" content="noindex, nofollow">` to the document
 * head while mounted, and restores the previous value on unmount. Used by
 * the catch-all fallback so unknown URLs (which the SPA still serves as
 * HTTP 200) are treated as soft-404s by search engines.
 */
function NoIndexMeta() {
  useEffect(() => {
    let existing = document.querySelector('meta[name="robots"]');
    const previous = existing ? existing.getAttribute('content') : null;
    if (!existing) {
      existing = document.createElement('meta');
      existing.setAttribute('name', 'robots');
      document.head.appendChild(existing);
    }
    existing.setAttribute('content', 'noindex, nofollow');
    return () => {
      if (previous !== null && existing) {
        existing.setAttribute('content', previous);
      }
    };
  }, []);
  return null;
}
